import { useState } from "react";
import { Box, Fade, IconButton, Modal, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { common, financedEmissions } from "../../../i18n";
import { EmissionYear } from "../../../models/dto/cat15/cat15.types";
import { formatNumber } from "../format";

interface Props {
  open: boolean;
  onClose: () => void;
  borrowerName: string;
  /** Row whose certificate is shown; null when closed. */
  row: EmissionYear | null;
  /** Open the file picker to replace the current certificate. */
  onReplace: () => void;
  /** Soft-delete the current certificate (parent confirms first). */
  onDelete: () => void;
}

const ZOOM_STEP = 0.25;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;

const fileExt = (name: string) => {
  const ext = name.includes(".") ? name.slice(name.lastIndexOf(".") + 1) : "";
  return ext ? ext.toUpperCase() : "FILE";
};
const isImage = (name: string) => /\.(png|jpe?g|gif|webp|svg)$/i.test(name);

const formatSize = (bytes: number | null) => {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(2)}MB` : `${Math.max(1, Math.round(bytes / 1024))}KB`;
};

// Figma shows DD/MM/YYYY (e.g. 01/08/2026), distinct from the table's ISO date.
const formatDmy = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const InfoDot = () => (
  <Box component="img" src="/img/info-circle.svg" alt="" width={12} height={12} flexShrink={0} />
);

/**
 * Certificate Information modal — Figma SME-D-Bank frame 6661:18685 (left,
 * Document Preview) + 6661:18699 (right, Certificate Information). Two 560px
 * columns: a zoomable document preview and the read-only borrower/cert details
 * with download / delete / replace actions. Values are pulled from Dev Mode.
 */
const CertificateInfoModal = ({ open, onClose, borrowerName, row, onReplace, onDelete }: Props) => {
  const { t } = useTranslation([financedEmissions, common], { nsMode: "fallback" });
  const [zoom, setZoom] = useState(1);
  if (!row) return null;

  const zoomOut = () => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));
  const zoomIn = () => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
  const zoomReset = () => setZoom(1);

  const fileName = row.certificateFileName ?? "";
  const fileUrl = row.certificateUrl;
  const meta = [
    fileName ? fileExt(fileName) : "",
    formatSize(row.certificateSize),
    row.certificateUploadedAt ? t("Cert-Uploaded-On", { date: formatDmy(row.certificateUploadedAt) }) : "",
  ]
    .filter(Boolean)
    .join(" · ");

  const onDownload = () => {
    if (fileUrl) window.open(fileUrl, "_blank", "noopener");
  };

  // Read-only field: label (+ optional required *) + info dot, then a disabled-looking value box.
  const Field = ({ label, value, required }: { label: string; value: string; required?: boolean }) => (
    <Box display="flex" flexDirection="column" gap="8px" width="100%">
      <Box display="flex" alignItems="center" gap="6px">
        <Typography fontSize="16px" lineHeight="24px" fontWeight={500} color="var(--dark)">
          {label}
          {required && <Box component="span" color="var(--red)">{" *"}</Box>}
        </Typography>
        <InfoDot />
      </Box>
      <Box px="12px" py="8px" borderRadius="8px" bgcolor="var(--gray5)" border="1px solid var(--gray5)" width="100%">
        <Typography fontSize="16px" lineHeight="24px" fontWeight={400} color="var(--dark)">
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Fade in={open} timeout={300}>
        <Box
          position="absolute"
          top="50%"
          left="50%"
          width="1120px"
          maxWidth="96dvw"
          maxHeight="92dvh"
          borderRadius="8px"
          bgcolor="var(--white)"
          sx={{
            transform: "translate(-50%, -50%)",
            outline: "none",
            boxShadow: 24,
            // One scroll for the whole modal so both columns move together (no split feel).
            overflowY: "auto",
            overflowX: "hidden",
            // Figma letterSpacing is 0 everywhere; MUI Typography adds a default — zero it out.
            "& .MuiTypography-root": { letterSpacing: 0 },
          }}
        >
          {/* Inner flex row sizes to content height so the outer box scrolls both columns as one. */}
          <Box display="flex" alignItems="stretch" width="100%" minHeight="100%">
          {/* LEFT — Document Preview (Figma 6661:18685) */}
          <Box
            display="flex"
            flexDirection="column"
            gap="20px"
            flex={1}
            minWidth={0}
            p="40px"
            bgcolor="var(--gray8)"
          >
            <Box display="flex" alignItems="center" gap="8px" width="100%">
              <Box width="2px" height="16px" borderRadius="2px" bgcolor="var(--dark)" flexShrink={0} />
              <Typography fontSize="16px" lineHeight="24px" fontWeight={500} color="var(--dark)">
                {t("Cert-Preview-Title")}
              </Typography>
            </Box>

            <Box display="flex" flex={1} width="100%" minHeight={0}>
              {/* A4 surface — the cert renders here. */}
              <Box position="relative" flex={1} minWidth={0} bgcolor="var(--doc-preview-surface)" overflow="hidden">
                {!fileUrl ? (
                  <Box
                    position="absolute"
                    sx={{ inset: 0 }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    px="24px"
                  >
                    <Typography fontSize="14px" fontWeight={400} color="var(--gray1)">
                      {t("Cert-Preview-Empty")}
                    </Typography>
                  </Box>
                ) : isImage(fileName) ? (
                  // Image: our scroll container + custom zoom bar (no native viewer for images).
                  <>
                    <Box
                      position="absolute"
                      sx={{
                        inset: 0,
                        overflow: "auto",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box width="100%" sx={{ transform: `scale(${zoom})`, transformOrigin: "top center" }}>
                        <Box
                          component="img"
                          src={fileUrl}
                          alt={t("Cert-Preview-Title")}
                          sx={{ display: "block", width: "100%", height: "auto" }}
                        />
                      </Box>
                    </Box>
                    <Box
                      position="absolute"
                      bottom={0}
                      left={0}
                      right={0}
                      display="flex"
                      gap="16px"
                      alignItems="flex-end"
                      justifyContent="center"
                      py="20px"
                    >
                      <IconButton size="small" onClick={zoomOut} sx={{ p: 0 }} aria-label={t("Zoom-Out")}>
                        <Box component="img" src="/img/zoom-out.svg" alt="" width={24} height={24} />
                      </IconButton>
                      <IconButton size="small" onClick={zoomReset} sx={{ p: 0 }} aria-label={t("Zoom-Reset")}>
                        <Box component="img" src="/img/zoom-fit.svg" alt="" width={24} height={24} />
                      </IconButton>
                      <IconButton size="small" onClick={zoomIn} sx={{ p: 0 }} aria-label={t("Zoom-In")}>
                        <Box component="img" src="/img/zoom-in.svg" alt="" width={24} height={24} />
                      </IconButton>
                    </Box>
                  </>
                ) : (
                  // PDF: browser's native viewer fills the area. #toolbar=0&navpanes=0 hides the
                  // dark viewer chrome (Chrome/Edge) so only the document shows; scroll still works.
                  <Box
                    component="iframe"
                    src={`${fileUrl}#toolbar=0&navpanes=0`}
                    title={t("Cert-Preview-Title")}
                    sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* RIGHT — Certificate Information (Figma 6661:18699) */}
          <Box
            display="flex"
            flexDirection="column"
            gap="20px"
            flex={1}
            minWidth={0}
            p="40px"
            bgcolor="var(--white)"
            sx={{ overflowX: "hidden" }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Typography fontSize="20px" lineHeight="24px" fontWeight={700} color="var(--dark)">
                {t("Cert-Info-Title")}
              </Typography>
              <IconButton size="small" onClick={onClose} sx={{ p: 0 }}>
                <Box component="img" src="/img/close.svg" alt="" width={24} height={24} />
              </IconButton>
            </Box>

            <Box display="flex" flexDirection="column" gap="20px" width="100%">
              <Box display="flex" alignItems="center" gap="8px">
                <Typography fontSize="18px" lineHeight="24px" fontWeight={600} color="var(--dark)">
                  {t("Cert-Borrower-Data")}
                </Typography>
                <InfoDot />
              </Box>
              <Field label={t("Cert-Field-Borrower")} value={borrowerName} />
              <Field label={t("Col-Year")} value={String(row.year)} />
              <Field label={t("Cert-Field-Scope1")} value={formatNumber(row.scope1)} required />
              <Field label={t("Cert-Field-Scope2")} value={formatNumber(row.scope2)} required />
              <Field label={t("Col-Total-S1-S2")} value={formatNumber(row.s1s2)} />
              <Field label={t("Cert-Field-Verifier")} value={row.certificateVerifier ?? ""} required />
              <Field
                label={t("Cert-Field-Verification-Date")}
                value={formatDmy(row.certificateVerificationDate)}
                required
              />
            </Box>

            <Typography fontSize="18px" lineHeight="24px" fontWeight={600} color="var(--dark)">
              {t("Cert-Current-File")}
            </Typography>
            <Box
              display="flex"
              alignItems="center"
              gap="8px"
              p="20px"
              borderRadius="8px"
              bgcolor="var(--white)"
              width="100%"
              sx={{ border: "1px solid var(--dark-10)" }}
            >
              <Box component="img" src="/img/file-pdf.svg" alt="" width={64} height={64} flexShrink={0} />
              <Box display="flex" flexDirection="column" gap="4px" flex={1} minWidth={0}>
                <Typography
                  fontSize="16px"
                  lineHeight="24px"
                  fontWeight={500}
                  color="var(--dark)"
                  sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {fileName || "—"}
                </Typography>
                <Typography fontSize="12px" lineHeight="24px" fontWeight={400} color="var(--gray1)">
                  {meta}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap="8px" flexShrink={0}>
                <IconButton
                  onClick={onDownload}
                  aria-label={t("Download")}
                  sx={{ p: "8px", borderRadius: "4px", border: "1px solid var(--gray5)" }}
                >
                  <Box component="img" src="/img/download.svg" alt="" width={24} height={24} />
                </IconButton>
                <IconButton
                  onClick={onDelete}
                  aria-label={t("Delete")}
                  sx={{ p: "8px", borderRadius: "4px", border: "1px solid var(--gray5)" }}
                >
                  <Box component="img" src="/img/delete.svg" alt="" width={24} height={24} />
                </IconButton>
              </Box>
            </Box>

            <Box
              component="button"
              onClick={onReplace}
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap="8px"
              px="12px"
              py="8px"
              borderRadius="4px"
              bgcolor="var(--white)"
              width="100%"
              sx={{ border: "1px solid var(--gray1)", cursor: "pointer" }}
            >
              <Box component="img" src="/img/refresh-2.svg" alt="" width={24} height={24} />
              <Typography fontSize="16px" lineHeight="24px" fontWeight={500} color="var(--dark)">
                {t("Cert-Replace-File")}
              </Typography>
            </Box>
          </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default CertificateInfoModal;
